import React from "react";

/**
 * SeoContentBlock — the "About this page" section that lives at the bottom of
 * marketing pages to give crawlers real, useful text content.
 *
 * Not a keyword-stuffer. Every section is written to add educational value
 * for a real visitor while also raising the page's word count above the
 * 300-word threshold search auditors flag.
 *
 * Layout: single-column, muted background, restrained typography. Sits
 * above the footer and reads as an editorial "learn more" section.
 *
 * Props:
 *   eyebrow   — small uppercase label above the heading
 *   heading   — H2
 *   sections  — [{ heading: string (H3), body: string | ReactNode }]
 *   footNote  — optional italic paragraph at the bottom
 */
export default function SeoContentBlock({ eyebrow, heading, sections = [], footNote }) {
  if (!sections.length) return null;
  return (
    <section
      className="border-t border-slate-200 bg-slate-50"
      data-testid="seo-content-block"
    >
      <div className="max-w-4xl mx-auto px-6 py-12 lg:py-16">
        {eyebrow && (
          <p className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">
            {eyebrow}
          </p>
        )}
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
          {heading}
        </h2>
        <div className="mt-8 space-y-8">
          {sections.map((s, i) => (
            <div key={s.heading || i}>
              {s.heading && (
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                  {s.heading}
                </h3>
              )}
              {typeof s.body === "string" ? (
                <p className="text-slate-700 leading-relaxed text-[15px]">{s.body}</p>
              ) : (
                s.body
              )}
            </div>
          ))}
        </div>
        {footNote && (
          <p className="mt-8 text-xs text-slate-500 italic leading-relaxed">
            {footNote}
          </p>
        )}
      </div>
    </section>
  );
}
